import React, { useState } from 'react';
import { Button, Card, EmptyValue, Form, Modal, Table } from '@/components/ui';
import { Vehicle } from '../../../types/Vehicle';
import vehicleService, { fieldErrorsOf } from '../../../services/vehicleService';
import { formatDate, formatDateTime } from '../../../utils/wrapDate';
import {
  cvipDueState,
  formatLastReportedDateTime,
  formatMeterReading,
  formatNextPmLine,
  isGreasingOverdue,
  meterUnitOf,
  odometerSourceLabel
} from '../utils/equipmentDisplay';

interface EquipmentMaintenanceTabProps {
  vehicle: Vehicle;
  /** Manage Repairs: Correct Reading (EQ-19). */
  canCorrectOdometer: boolean;
  onUpdated: () => Promise<void>;
}

interface DefinitionRow {
  label: string;
  value: React.ReactNode;
  tone?: 'danger' | 'soon';
}

const DefinitionList: React.FC<{ rows: DefinitionRow[] }> = ({ rows }) => (
  <dl className="tw-mb-0 tw-divide-y tw-divide-line">
    {rows.map((row) => (
      <div key={row.label} className="tw-grid tw-grid-cols-1 tw-gap-1 tw-px-4 tw-py-3 sm:tw-grid-cols-3 sm:tw-gap-4">
        <dt className="tw-text-sm tw-font-semibold tw-text-content-secondary">{row.label}</dt>
        <dd
          className={`tw-mb-0 tw-text-sm tw-tabular-nums sm:tw-col-span-2 ${
            row.tone === 'danger' ? 'tw-font-semibold tw-text-danger' : row.tone === 'soon' ? 'tw-font-semibold tw-text-warning-text' : 'tw-text-content'
          }`}
        >
          {row.value}
        </dd>
      </div>
    ))}
  </dl>
);

const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-danger" role="alert">{message}</p> : null;

/** Maintenance tab: odometer with Correct Reading (EQ-19) and upcoming service, including CVIP (EQ-01). */
const EquipmentMaintenanceTab: React.FC<EquipmentMaintenanceTabProps> = ({ vehicle, canCorrectOdometer, onUpdated }) => {
  const unit = meterUnitOf(vehicle);
  const [correcting, setCorrecting] = useState(false);
  const [value, setValue] = useState('');
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const readings = vehicle.odometerReadings || [];
  const greasingOverdue = isGreasingOverdue(vehicle);
  const nextPm = formatNextPmLine(vehicle);
  const cvipState = cvipDueState(vehicle.nextCVIP);
  const cvipDate = formatDate(vehicle.nextCVIP);
  const backwards = vehicle.currentOdometer != null && value !== '' && Number(value) < vehicle.currentOdometer;

  const openCorrect = () => {
    setValue('');
    setOverride(false);
    setReason('');
    setErrors({});
    setCorrecting(true);
  };

  const save = async () => {
    const next: Record<string, string> = {};
    if (value.trim() === '' || !/^\d+$/.test(value.trim())) next.value = 'Enter a whole number, 0 or more';
    else if (backwards && !override) {
      next.value = `Reading is lower than the current ${formatMeterReading(vehicle.currentOdometer, unit)}. Tick Override to record it.`;
    }
    if (override && !reason.trim()) next.reason = 'Reason required for an override';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSaving(true);
    try {
      await vehicleService.recordOdometer(vehicle.id, { value: Number(value), override, reason: reason.trim() || undefined });
      setCorrecting(false);
      setNotice(`Reading of ${formatMeterReading(Number(value), unit)} recorded.`);
      await onUpdated();
    } catch (err) {
      setErrors(fieldErrorsOf(err, 'value'));
    } finally {
      setSaving(false);
    }
  };

  const odometerRows: DefinitionRow[] = [
    { label: 'Current Reading', value: formatMeterReading(vehicle.currentOdometer, unit) },
    { label: 'Last Reported', value: formatLastReportedDateTime(vehicle.lastOdometerReportedDateTime || readings[0]?.recordedAt) },
    { label: 'Source', value: odometerSourceLabel(vehicle.lastOdometerReportedType || readings[0]?.source) },
    { label: 'Reported By', value: readings[0]?.reporterName || <EmptyValue /> }
  ];

  const upcomingRows: DefinitionRow[] = [
    { label: 'Next PM', value: nextPm.text, tone: nextPm.overdue ? 'danger' : undefined },
    {
      label: 'Next Greasing',
      value: greasingOverdue
        ? `${formatMeterReading(vehicle.nextGreasing, unit)} – overdue`
        : formatMeterReading(vehicle.nextGreasing, unit),
      tone: greasingOverdue ? 'danger' : undefined
    },
    {
      label: 'Next CVIP',
      value: cvipDate ? `${cvipDate}${cvipState === 'overdue' ? ' – overdue' : cvipState === 'soon' ? ' – due soon' : ''}` : <EmptyValue />,
      tone: cvipState === 'overdue' ? 'danger' : cvipState === 'soon' ? 'soon' : undefined
    },
    { label: 'Next Minor', value: formatMeterReading(vehicle.nextMinor, unit) },
    { label: 'Next Major', value: formatMeterReading(vehicle.nextMajor, unit) }
  ];

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Card data-tour="odometer-block">
        <Card.Header className="tw-flex tw-items-center tw-justify-between tw-gap-2">
          <h2 className="tw-mb-0 tw-text-base tw-font-semibold">Odometer</h2>
          {canCorrectOdometer && (
            <Button size="sm" variant="outline-secondary" onClick={openCorrect}>
              <i className="bi bi-pencil tw-mr-1" aria-hidden="true" />
              Correct Reading
            </Button>
          )}
        </Card.Header>
        {notice && <p role="status" className="tw-mb-0 tw-border-b tw-border-line tw-px-4 tw-py-2 tw-text-sm tw-text-success">{notice}</p>}
        <DefinitionList rows={odometerRows} />
        <div className="tw-border-t tw-border-line">
          <h3 className="tw-mb-0 tw-px-4 tw-pt-4 tw-text-sm tw-font-semibold tw-text-content">Reading History</h3>
          {readings.length === 0 ? (
            <p className="tw-mb-0 tw-p-4 tw-text-sm tw-text-content-muted">No readings recorded yet.</p>
          ) : (
            <Table responsive className="tw-mb-0">
              <thead>
                <tr>
                  <th>When</th>
                  <th className="tw-text-right">Reading</th>
                  <th>Source</th>
                  <th>Reported By</th>
                  <th>Override Reason</th>
                </tr>
              </thead>
              <tbody>
                {readings.map((reading) => (
                  <tr key={reading.id}>
                    <td className="tw-whitespace-nowrap">{formatDateTime(reading.recordedAt) ?? <EmptyValue />}</td>
                    <td className="tw-whitespace-nowrap tw-text-right tw-tabular-nums">{formatMeterReading(reading.value, unit)}</td>
                    <td>{odometerSourceLabel(reading.source)}</td>
                    <td>{reading.reporterName || <EmptyValue />}</td>
                    <td>{reading.override ? reading.reason || 'Override (no reason recorded)' : <EmptyValue />}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>

      <Card>
        <Card.Header>
          <h2 className="tw-mb-0 tw-text-base tw-font-semibold">Upcoming Service</h2>
        </Card.Header>
        <DefinitionList rows={upcomingRows} />
      </Card>

      <Modal show={correcting} onHide={() => setCorrecting(false)} size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Correct Reading</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-sm tw-text-content-secondary">Current: {formatMeterReading(vehicle.currentOdometer, unit)}</p>
          <Form.Label htmlFor="odometer-value">
            New Reading ({unit}) <span className="tw-text-danger" aria-hidden="true">*</span>
          </Form.Label>
          <Form.Control id="odometer-value" inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value)} />
          <FieldError message={errors.value} />
          <Form.Check
            className="tw-mt-3"
            id="odometer-override"
            type="checkbox"
            label="Override a backwards reading"
            checked={override}
            onChange={(e) => setOverride(e.target.checked)}
          />
          {override && (
            <>
              <Form.Label htmlFor="odometer-reason" className="tw-mt-3">
                Reason <span className="tw-text-danger" aria-hidden="true">*</span>
              </Form.Label>
              <Form.Control id="odometer-reason" as="textarea" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
              <FieldError message={errors.reason} />
            </>
          )}
          <FieldError message={errors._form} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setCorrecting(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Reading'}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EquipmentMaintenanceTab;
